import { Button } from "@/components/ui/button";
import { CuisineSelector } from "./CuisineSelector";
import { DishTypeSelector } from "./DishTypeSelector";
import { TagSelector } from "./TagSelector";
import { IngredientInput } from "./IngredientInput";
import { Label } from "@/components/ui/label";
import { NutritionalFields } from "./NutritionalFields";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { InfoIcon } from "lucide-react";
import { PortionSizeInput } from "./PortionSizeInput";
interface Step3Props {
  formData: {
    cuisine_type: string;
    meal_type: string;
    dietary_tags: string[];
    allergens: string[];
    ingredients: string[];
    calories: string;
    carbs: string;
    protein: string;
    fat: string;
    portion_size_value: string;
    portion_size_unit: string;
  };
  onChange: (field: string, value: any) => void;
  onReview: () => void;
  onBack: () => void;
}
export const Step3Details = ({
  formData,
  onChange,
  onReview,
  onBack
}: Step3Props) => {
  const canProceed = formData.cuisine_type && formData.ingredients.length > 0 && formData.meal_type;
  const renderTooltip = (label: string, content: string) => <span className="inline-flex items-center space-x-1">
      <span>{label}</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" className="h-4 w-4 p-0 hover:bg-transparent">
            <InfoIcon className="h-4 w-4 text-gray-500" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="max-w-[280px] text-sm bg-white p-2 rounded-lg shadow-lg z-[60] break-words" side="top" align="center" sideOffset={5}>
          <p>{content}</p>
        </PopoverContent>
      </Popover>
    </span>;
  return <div className="space-y-6 px-0">
      <div className="space-y-2.5">
        <Label className="text-black font-medium text-base">
          {renderTooltip("Dish Type *", "Select the category that best describes when this dish is typically served.")}
        </Label>
        <DishTypeSelector dishType={formData.meal_type} onDishTypeChange={value => onChange("meal_type", value)} />
      </div>

      <div className="space-y-2.5">
        <Label className="text-black font-medium text-base">Cuisine Type *</Label>
        <CuisineSelector cuisineType={formData.cuisine_type} onCuisineChange={cuisine => onChange("cuisine_type", cuisine)} />
      </div>

      <div className="space-y-2.5">
        <Label className="text-black font-medium text-base">Dietary Tags</Label>
        <TagSelector label="dietary tags" selectedTags={formData.dietary_tags} onTagsChange={tags => onChange("dietary_tags", tags)} variant="default" showNone={true} />
      </div>

      <div className="space-y-2.5">
        <Label className="text-black font-medium text-base">Allergens</Label>
        <TagSelector label="allergens" selectedTags={formData.allergens} onTagsChange={allergens => onChange("allergens", allergens)} variant="destructive" showNone={true} />
      </div>

      <div className="space-y-2.5">
        <Label className="text-black font-medium text-base">
          {renderTooltip("Ingredients *", "List all ingredients used in your dish. This helps customers with dietary restrictions make informed decisions. Press 'Enter' to add each ingredient.")}
        </Label>
        <IngredientInput ingredients={formData.ingredients} onAddIngredient={(ingredient: string) => onChange("ingredients", [...formData.ingredients, ingredient])} onRemoveIngredient={(ingredient: string) => onChange("ingredients", formData.ingredients.filter(i => i !== ingredient))} />
      </div>

      <div className="space-y-2.5">
        <Label className="text-black font-medium text-xl">
          {renderTooltip("Nutritional Information", "Provide the nutritional content per serving. This helps customers track their dietary intake. Provide numbers only (i.e. '45', not '45g').")}
        </Label>
        <NutritionalFields calories={formData.calories} carbs={formData.carbs} protein={formData.protein} fat={formData.fat} onChange={onChange} />
      </div>

      <div className="space-y-2.5">
        
        <PortionSizeInput value={formData.portion_size_value} unit={formData.portion_size_unit} onValueChange={value => onChange("portion_size_value", value)} onUnitChange={unit => onChange("portion_size_unit", unit)} />
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} className="px-8 py-3 h-12 rounded-full text-base font-medium border-[#E5E5E5] hover:bg-[#F9FAFB] text-black">
          Back
        </Button>
        <Button onClick={onReview} disabled={!canProceed} className="bg-black hover:bg-black/90 text-white px-8 py-3 h-12 rounded-full text-base font-medium transition-colors disabled:bg-[#E5E5E5]">
          Review Meal
        </Button>
      </div>
    </div>;
};